import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  FaPaperPlane, 
  FaSearch, 
  FaEllipsisV, 
  FaArrowLeft, 
  FaClock, 
  FaCheck, 
  FaCheckDouble, 
  FaImage, 
  FaExclamationTriangle, 
  FaExchangeAlt, 
  FaTimes, 
  FaPaperclip, 
  FaUser, 
  FaEnvelope, 
  FaPhone, 
  FaSmile,
  FaTrash,
  FaCircle
} from 'react-icons/fa';
import Layout from '../components/layout/Layout';
import { useMessages } from '../contexts/MessageContext';
import { useAuth } from '../contexts/AuthContext';
import './Messages.css';

const getAvatarDisplayName = (user) => {
  if (!user) return 'Unknown User';
  if (user.first_name || user.last_name) {
    return `${user.first_name || ''} ${user.last_name || ''}`.trim();
  }
  return user.email || 'Unknown User';
};

const getStyledDisplayName = (user) => {
  if (!user) return React.createElement("span", { 
    className: "unknown-user-text",
    style: { 
      fontStyle: 'italic',
      opacity: 0.7,
      fontSize: '12px'
    }
  }, 'Unknown User');
  
  if (user.first_name || user.last_name) {
    return `${user.first_name || ''} ${user.last_name || ''}`.trim();
  }
  
  return React.createElement("span", {
    className: "user-email-text",
    style: {
      fontStyle: 'italic',
      opacity: 0.8,
      fontSize: '13px'
    }
  }, user.email);
};

const getAvatarInitial = (user) => {
  const name = getAvatarDisplayName(user);
  const parts = name.split(' ');
  if (parts.length >= 2) {
    return parts[0].charAt(0) + parts[1].charAt(0);
  }
  return name.charAt(0).toUpperCase() || 'U';
};

const getMessageStatusIcon = (status) => {
  switch (status) {
    case 'sent':
      return <FaCheck className="status-icon" />;
    case 'delivered':
      return <FaCheck className="status-icon" />;
    case 'read':
      return <FaCheckDouble className="status-icon status-read" />;
    default:
      return null;
  }
};

const Messages = () => {
  const { conversationId } = useParams();
  const navigate = useNavigate();
  const { 
    conversations, 
    loading, 
    error, 
    getConversationById, 
    sendMessage, 
    deleteConversation, 
    fetchThreadById, 
    markAsRead 
  } = useMessages();
  const { currentUser } = useAuth();
  
  const [activeConversation, setActiveConversation] = useState(null);
  const [messageText, setMessageText] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [sendingMessage, setSendingMessage] = useState(false);
  const [showMobileChat, setShowMobileChat] = useState(false);
  const [showDefaultHeader, setShowDefaultHeader] = useState(true);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    console.log('Header visibility check:', { activeConversation, showMobileChat, conversationId });
    if (conversationId && conversations.length > 0) {
      const conversation = getConversationById(parseInt(conversationId));
      if (conversation) {
        setActiveConversation(conversation);
        setShowMobileChat(true); // Show mobile chat when conversation is selected
        markAsRead(conversation.id);
      } else {
        fetchThreadById(conversationId).then(data => {
          if (data) {
            setActiveConversation(data);
            setShowMobileChat(true); // Show mobile chat when conversation is loaded
          }
        });
      }
    } else {
      setActiveConversation(null);
      setShowMobileChat(false); // Hide mobile chat when no conversation
    }
  }, [conversationId, conversations, getConversationById, fetchThreadById, markAsRead]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [activeConversation?.messages]);

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!messageText.trim() || !activeConversation || sendingMessage) return;

    setSendingMessage(true);
    try {
      const updatedConversation = await sendMessage(activeConversation.id, messageText.trim());
      setActiveConversation(updatedConversation);
      setMessageText('');
    } catch (error) {
      console.error('Failed to send message:', error);
    } finally {
      setSendingMessage(false);
    }
  };

  const handleDeleteConversation = async () => {
    if (!activeConversation) return;
    
    try {
      await deleteConversation(activeConversation.id);
      setShowDeleteModal(false);
      setActiveConversation(null);
      navigate('/messages');
    } catch (error) {
      console.error('Failed to delete conversation:', error);
    }
  };

  const formatMessageTime = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    const now = new Date();
    const diffInHours = (now - date) / (1000 * 60 * 60);
    
    if (diffInHours < 24) {
      return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
    } else if (diffInHours < 48) {
      return 'Yesterday';
    } else {
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    }
  };

  const filteredConversations = conversations.filter(convo => {
    const otherUser = convo.participants?.find(p => p.id !== currentUser?.id);
    const displayName = getAvatarDisplayName(otherUser);
    return displayName.toLowerCase().includes(searchQuery.toLowerCase());
  });

  const ConversationItem = ({ convo }) => {
    const isActive = activeConversation?.id === convo.id;
    const otherUser = convo.participants?.find(p => p.id !== currentUser?.id);
    const displayName = getAvatarDisplayName(otherUser);
    const lastMessage = convo.messages?.[convo.messages.length - 1];
    const isUnread = !convo.is_read;

    return (
      <div 
        className={`conversation-item ${isActive ? 'active' : ''}`}
        onClick={() => navigate(`/messages/${convo.id}`)}
      >
        <div className="conversation-avatar-wrapper">
          <div className="conversation-avatar">
            {getAvatarInitial(otherUser)}
          </div>
          <span className="online-indicator"></span>
        </div>
        
        <div className="conversation-details">
          <div className="conversation-header">
            <h4 className="conversation-name">{displayName}</h4>
            <span className="conversation-time">
              {lastMessage ? formatMessageTime(lastMessage.timestamp) : ''}
            </span>
          </div>
          
          <div className="conversation-preview">
            <p className={`last-message ${isUnread ? 'unread' : ''}`}>
              {lastMessage?.content || 'No messages yet'}
            </p>
            {isUnread && <span className="unread-badge">1</span>}
          </div>
        </div>
      </div>
    );
  };

  const MessageBubble = ({ message }) => {
    const isOwn = message.sender === currentUser?.id;
    const sender = activeConversation?.participants?.find(p => p.id === message.sender);

    return (
      <div className={`message-wrapper ${isOwn ? 'own-message' : 'other-message'}`}>
        {!isOwn && (
          <div className="message-avatar">
            {getAvatarInitial(sender)}
          </div>
        )}
        
        <div className="message-bubble">
          <div className="message-content">{message.content}</div>
          <div className="message-meta">
            <span className="message-time">{formatMessageTime(message.timestamp)}</span>
            {isOwn && getMessageStatusIcon(message.status)}
          </div>
        </div>
      </div>
    );
  };

  const DeleteModal = () => {
    if (!showDeleteModal) return null;
    
    return (
      <div className="modal-overlay" onClick={() => setShowDeleteModal(false)}>
        <div className="modal-content" onClick={(e) => e.stopPropagation()}>
          <div className="modal-header">
            <h3>Delete Conversation</h3>
            <button className="modal-close" onClick={() => setShowDeleteModal(false)}>
              <FaTimes />
            </button>
          </div>
          <div className="modal-body">
            <p>Are you sure you want to delete this conversation? This action cannot be undone.</p>
          </div>
          <div className="modal-footer">
            <button className="btn-secondary" onClick={() => setShowDeleteModal(false)}>
              Cancel
            </button>
            <button className="btn-danger" onClick={handleDeleteConversation}>
              Delete
            </button>
          </div>
        </div>
      </div>
    );
  };

  const createChatHeader = () => {
    const otherUser = activeConversation?.participants?.find(p => p.id !== currentUser?.id);
    console.log('Header user data:', { otherUser, activeConversation, currentUser });
    const displayName = getAvatarDisplayName(otherUser);

    return React.createElement("div", {
      className: "custom-mobile-chat-header"
    },
      React.createElement("button", {
        className: "custom-back-button",
        onClick: () => {
          console.log('Back button clicked, showMobileChat:', showMobileChat);
          setShowMobileChat(false);
          navigate('/messages');
        }
      }, React.createElement(FaArrowLeft, null)),
      
      React.createElement("div", {
        className: "custom-chat-avatar"
      }, getAvatarInitial(otherUser)),
      
      React.createElement("div", {
        className: "custom-chat-info"
      },
        React.createElement("h3", null, getAvatarDisplayName(otherUser)),
        React.createElement("span", {
          className: "custom-chat-status"
        }, 
          React.createElement(FaCircle, { className: "custom-status-dot" }),
          " Active now"
        )
      ),
      
      React.createElement("div", {
        className: "custom-chat-actions"
      },
        React.createElement("button", {
          className: "custom-action-button",
          onClick: () => setShowDeleteModal(true)
        }, React.createElement(FaTrash, null))
      )
    );
  };

  return (
    <Layout>
      {/* Custom Mobile Chat Header - Always show when conversation is active */}
      {activeConversation && showMobileChat && createChatHeader()}
      
      <div className="messages-page">
        <div className={`conversations-panel ${showMobileChat ? 'mobile-hidden' : ''}`}>
          {/* Sidebar Header */}
          <div className="sidebar-header">
            <h2 className="sidebar-title">Messages</h2>
            <div className="sidebar-actions">
              <button className="icon-button">
                <FaEllipsisV />
              </button>
            </div>
          </div>

          {/* Search Bar */}
          <div className="search-wrapper">
            <div className="search-input-wrapper">
              <FaSearch className="search-icon" />
              <input
                type="text"
                className="search-input"
                placeholder="Search conversations..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
          </div>

          {/* Conversations List */}
          <div className="conversations-list">
            {loading ? (
              <div className="loading-state">
                <div className="spinner"></div>
                <p>Loading conversations...</p>
              </div>
            ) : error ? (
              <div className="error-state">
                <p>{error}</p>
              </div>
            ) : filteredConversations.length === 0 ? (
              <div className="empty-state">
                <p>No conversations found</p>
              </div>
            ) : (
              filteredConversations.map(convo => (
                <ConversationItem key={convo.id} convo={convo} />
              ))
            )}
          </div>
        </div>

        {/* Chat Panel */}
        <div className={`chat-panel ${showMobileChat ? 'mobile-visible' : ''}`}>
          {!activeConversation ? (
            <div className="empty-chat-state">
              <div className="empty-chat-icon">
                <FaPaperPlane />
              </div>
              <h3>Select a conversation</h3>
              <p>Choose a conversation from the list to start messaging</p>
            </div>
          ) : (
            <>
              {/* Messages Area */}
              <div className="messages-area">
                <div className="messages-container">
                  {activeConversation.messages?.map(message => (
                    <MessageBubble key={message.id} message={message} />
                  ))}
                  <div ref={messagesEndRef} />
                </div>
              </div>

              {/* Message Input */}
              <div className="message-input-area">
                <form onSubmit={handleSendMessage} className="message-form">
                  <button type="button" className="input-icon-button">
                    <FaPaperclip />
                  </button>
                  
                  <input
                    type="text"
                    className="message-input"
                    placeholder="Type a message..."
                    value={messageText}
                    onChange={(e) => setMessageText(e.target.value)}
                    disabled={sendingMessage}
                  />
                  
                  <button type="button" className="input-icon-button">
                    <FaSmile />
                  </button>
                  
                  <button 
                    type="submit" 
                    className="send-button"
                    disabled={!messageText.trim() || sendingMessage}
                  >
                    {sendingMessage ? (
                      <div className="spinner small"></div>
                    ) : (
                      <FaPaperPlane />
                    )}
                  </button>
                </form>
              </div>
            </>
          )}
        </div>

        <DeleteModal />
      </div>
    </Layout>
  );
};

export default Messages;